import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SITE from '../../api';
import useSEO from '../../useSEO';
import { IMG, useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';

export default function ShopPosters() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.shop-posters.meta_title'] || 'Free Shop Posters for Opticians',
    description: c['tools.shop-posters.meta_description'] || 'Free high-resolution posters for opticians — patient education guides and shop-wall graphics, ready to download and print for your waiting room or store.',
    keywords: c['tools.shop-posters.meta_keywords'] || 'free optician posters, waiting room posters, optical shop posters, patient education graphics, printable eyewear posters',
    canonical: `${window.location.origin}/store/tools/shop-posters`,
  });

  const [posters, setPosters] = useState(null);
  const [category, setCategory] = useState('');

  useEffect(() => {
    SITE.get('/poster-gallery').then(r => setPosters(r.data.posters || [])).catch(() => setPosters([]));
  }, []);

  const categories = useMemo(() => {
    if (!posters) return [];
    return [...new Set(posters.map(p => p.category).filter(Boolean))];
  }, [posters]);

  const filtered = useMemo(() => {
    if (!posters) return [];
    return category ? posters.filter(p => p.category === category) : posters;
  }, [posters, category]);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 14, overflow: 'hidden' };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 16px', textAlign: 'center' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <h1 style={{ fontSize: 'clamp(24px,3.6vw,34px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '14px 0 12px' }}>
          Free Shop Posters
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          High-resolution posters for your waiting room or shop wall — download, print, and put them up today. No login, no cost.
        </p>
      </section>

      <section style={{ maxWidth: 1100, margin: '24px auto 0', padding: '0 24px 64px' }}>
        {categories.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 28 }}>
            <button onClick={() => setCategory('')} style={{
              border: !category ? '1.5px solid var(--sf-primary)' : '1.5px solid #e5e5e5',
              background: !category ? 'var(--sf-primary)' : '#fff', color: !category ? '#fff' : 'var(--sf-primary)',
              borderRadius: 20, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}>All</button>
            {categories.map(c => (
              <button key={c} onClick={() => setCategory(c)} style={{
                border: category === c ? '1.5px solid var(--sf-primary)' : '1.5px solid #e5e5e5',
                background: category === c ? 'var(--sf-primary)' : '#fff', color: category === c ? '#fff' : 'var(--sf-primary)',
                borderRadius: 20, padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              }}>{c}</button>
            ))}
          </div>
        )}

        {posters === null && <div style={{ textAlign: 'center', color: 'var(--sf-muted)', padding: '40px 0' }}>Loading posters…</div>}
        {posters && posters.length === 0 && <div style={{ textAlign: 'center', color: 'var(--sf-muted)', padding: '40px 0' }}>No posters available right now — check back soon.</div>}

        {filtered.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 22 }}>
            {filtered.map(p => (
              <div key={p.id} style={card}>
                <div style={{ aspectRatio: '3/4', background: '#f2f2f2', overflow: 'hidden' }}>
                  <img src={IMG(p.image_path)} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                </div>
                <div style={{ padding: '18px 18px 20px' }}>
                  {p.category && (
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--sf-hover)',
                      display: 'block', marginBottom: 6,
                    }}>{p.category}</span>
                  )}
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 8px' }}>{p.title}</h3>
                  {p.description && <p style={{ fontSize: 12.5, color: 'var(--sf-muted)', lineHeight: 1.6, margin: '0 0 14px' }}>{p.description}</p>}
                  <a href={IMG(p.image_path)} download
                    onClick={() => SITE.post(`/poster-gallery/${p.id}/download`).catch(() => {})}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--sf-primary)', color: '#fff',
                      borderRadius: 8, padding: '9px 16px', fontSize: 12.5, fontWeight: 700, textDecoration: 'none',
                    }}>
                    <i className="ti ti-download" style={{ fontSize: 14 }} /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>
    </div>
  );
}
